import React from 'react';
import {
  Box,
  Column,
  Flex,
  Loader,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { CreditCardDisplay } from 'src/components/selfService/common/Balance/components/CreditCardDisplay/CreditCardDisplay';
import { BalancePortalButton } from 'src/components/selfService/common/Balance/components/BalancePortalButton/BalancePortalButton';
import { PoweredByBalance } from 'src/components/selfService/common/Balance/components/PoweredByBalance/PoweredByBalance';
import { BalanceProvider } from 'src/components/selfService/common/Balance/context/BalanceProvider';
import { useBalanceContext } from 'src/components/selfService/common/Balance/hooks/useBalanceContext';
import { useFetchBalancePaymentMethodsCards } from 'src/components/selfService/common/Balance/hooks/useFetchBalancePaymentMethodsCards';
import { BalancePortalPage } from 'src/components/selfService/common/Balance/balance.types';
import { PageSectionTitle } from 'src/components/selfService/layout/PageSectionTitle/PageSectionTitle';
import { useScreenSizes } from 'src/utilities/useScreenSizes';

const copy = {
  title: 'Payment methods',
  description:
    'Your default payment method will be used for all transactions. Any backup methods will only be charged if the default method fails, or for outstanding invoices. You can manage changes and updates to payment methods in the dedicated Balance portal.',
  manageButton: 'Manage payment methods',
} as const;

function BalancePaymentMethodsSectionContent(): JSX.Element {
  const { isDesktop } = useScreenSizes();
  const { externalBalanceBuyerId } = useBalanceContext();
  const { isLoading, balancePaymentMethodsCards } =
    useFetchBalancePaymentMethodsCards(externalBalanceBuyerId);

  const cards = balancePaymentMethodsCards?.cards ?? [];

  if (isLoading) {
    return (
      <Box data-testid="balance-payment-methods-section">
        <Flex alignItems="center" justifyContent="center" minHeight="140px">
          <Loader size="md" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box data-testid="balance-payment-methods-section">
      <Row>
        <Column tablet={12} desktop={9}>
          <PageSectionTitle
            title={copy.title}
            subtitle={copy.description}
            headingVariant="h4"
            y="md"
          />
          <Stack y="lg">
            {cards.map((card) => (
              <CreditCardDisplay
                key={card.id}
                brand={card.brand}
                expiration={{
                  month: card.expirationMonth,
                  year: card.expirationYear,
                }}
                lastFourDigits={card.lastFourDigits}
                isDefault={card.isDefault}
              />
            ))}
          </Stack>
        </Column>
        <Column tablet={12} desktop={3}>
          <Flex
            alignItems={isDesktop ? 'flex-end' : 'flex-start'}
            justifyContent={isDesktop ? 'space-between' : 'flex-start'}
            flexDirection="column"
            height="100%"
          >
            <Stack y="sm" alignSelf={isDesktop ? 'flex-end' : 'stretch'}>
              <BalancePortalButton
                balancePortalPage={BalancePortalPage.PAYMENT_METHODS}
                text={copy.manageButton}
                block={!isDesktop}
              />
            </Stack>
            <PoweredByBalance />
          </Flex>
        </Column>
      </Row>
    </Box>
  );
}

export function BalancePaymentMethodsSection(): JSX.Element {
  return (
    <BalanceProvider>
      <BalancePaymentMethodsSectionContent />
    </BalanceProvider>
  );
}
